import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Paper,
  Container,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { supabase } from '../supabaseClient';
import { generateRandomName } from '../utils/nameGenerator';

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    birthday: '',
    acceptTerms: false,
    acceptEmails: false
  });
  const [error, setError] = useState('');

  // Check if user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate('/workflows'); // Redirect if already logged in
      }
    };
    checkSession();
  }, [navigate]); // Add navigate as a dependency
  
  useEffect(() => {
    if (location?.state?.message) {
      setError(location.state.message);
    }
  }, [location]);

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Lütfen geçerli bir e-posta adresi girin');
        return;
      }

      if (formData.password.length < 8) {
        setError('Şifre en az 8 karakter uzunluğunda olmalıdır');
        return;
      }

      const generatedUsername = generateRandomName();

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            username: generatedUsername,
            email_confirm: false,
            acceptEmails: formData.acceptEmails
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const { data: { user }, error: profileError } = await supabase.auth.getUser();
          if (!profileError && user) {
            const { error: insertError } = await supabase
              .from('profiles')
              .upsert([
                {
                  id: user.id,
                  username: generatedUsername,
                  email: formData.email,
                  first_name: formData.firstName,
                  last_name: formData.lastName,
                  birth_date: formData.birthday,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }
              ], {
                onConflict: 'id',
                returning: 'minimal'
              });
            if (insertError) throw insertError;
          }

          if (profileError) {
            console.warn('Profil oluşturma uyarısı:', profileError);
          }
          
          navigate('/login', { 
            state: { 
              message: 'Lütfen giriş yapmadan önce e-posta adresinizi onaylayın.',
              severity: 'success'
            }
          });
        } catch (profileError) {
          console.warn('Profil oluşturma uyarısı:', profileError);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/workflows`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      
      if (error) throw error;
      // Don't navigate here - let the OAuth redirect handle it
    } catch (error) {
      setError(error.message);
    }
  };

  const { theme } = useThemeMode();

  return (
    <ThemeProvider theme={theme}>
    <Box sx={{
      minHeight: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      background: theme => theme.palette.mode === 'dark' 
        ? '#1A1A2A' 
        : '#f5f7fa',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        background: theme => theme.palette.mode === 'dark'
          ? `radial-gradient(circle at center, rgba(0, 74, 173, 0.15) 0%, transparent 50%),
             repeating-linear-gradient(45deg, rgba(0, 74, 173, 0.08) 0px, rgba(0, 74, 173, 0.08) 2px, transparent 2px, transparent 10px),
             repeating-linear-gradient(-45deg, rgba(0, 74, 173, 0.08) 0px, rgba(0, 74, 173, 0.08) 2px, transparent 2px, transparent 10px)`
          : `radial-gradient(circle at center, rgba(0, 74, 173, 0.08) 0%, transparent 50%),
             repeating-linear-gradient(45deg, rgba(0, 74, 173, 0.03) 0px, rgba(0, 74, 173, 0.03) 2px, transparent 2px, transparent 10px),
             repeating-linear-gradient(-45deg, rgba(0, 74, 173, 0.03) 0px, rgba(0, 74, 173, 0.03) 2px, transparent 2px, transparent 10px)`,
        backgroundSize: '100% 100%, 20px 20px, 20px 20px',
        animation: 'gradientMove 20s ease-in-out infinite',
        zIndex: 0,
      },
      '@keyframes gradientMove': {
        '0%, 100%': { 
          backgroundPosition: '0% 0%, 0px 0px, 0px 0px',
          opacity: 0.8
        },
        '50%': { 
          backgroundPosition: '0% 0%, 10px 10px, -10px -10px',
          opacity: 1
        }
      }
    }}>
      <Container maxWidth="xs" sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: '440px' },
        mx: 'auto',
        px: { xs: 1, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 2, sm: 3 },
            margin: { xs: 1, sm: 2 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: '24px',
            backgroundColor: theme => theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
            border: theme => `1px solid ${theme.palette.divider}`,
            boxShadow: theme => `0 4px 24px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}
        >
          <Typography 
            component="h1" 
            variant="h5" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: 'primary.main',
              fontWeight: 600
            }}>
            Hesap Oluştur
          </Typography>

          <form onSubmit={handleSignup} style={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-posta Adresi"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Şifre"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              name="firstName"
              label="Ad"
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData({...formData, firstName: e.target.value})}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="lastName"
              label="Soyad"
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData({...formData, lastName: e.target.value})}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="birthday"
              label="Doğum Tarihi"
              type="date"
              id="birthday"
              InputLabelProps={{ shrink: true }}
              value={formData.birthday}
              onChange={(e) => setFormData({...formData, birthday: e.target.value})}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData({...formData, acceptTerms: e.target.checked})}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                      <Link 
                        to="https://sanalyapi.com/terms" 
                        style={{ 
                          textDecoration: 'none',
                          color: 'inherit',
                          fontWeight: 600,
                          position: 'relative'
                        }}
                        sx={{
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            width: '100%',
                            height: '2px',
                            bottom: -2,
                            left: 0,
                            backgroundColor: 'primary.main',
                            transform: 'scaleX(0)',
                            transformOrigin: 'right',
                            transition: 'transform 0.3s ease-in-out'
                          },
                          '&:hover::after': {
                            transform: 'scaleX(1)',
                            transformOrigin: 'left'
                          },
                          '&:hover': {
                            color: 'primary.main'
                          }
                        }}
                      >
                        Kullanım Koşulları
                      </Link>
                      <span>ve</span>
                      <Link 
                        to="https://sanalyapi.com/privacy-policy" 
                        style={{ 
                          textDecoration: 'none',
                          color: 'inherit',
                          fontWeight: 600,
                          position: 'relative'
                        }}
                        sx={{
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            width: '100%',
                            height: '2px',
                            bottom: -2,
                            left: 0,
                            backgroundColor: 'primary.main',
                            transform: 'scaleX(0)',
                            transformOrigin: 'right',
                            transition: 'transform 0.3s ease-in-out'
                          },
                          '&:hover::after': {
                            transform: 'scaleX(1)',
                            transformOrigin: 'left'
                          },
                          '&:hover': {
                            color: 'primary.main'
                          }
                        }}
                      >
                        Gizlilik Politikası
                      </Link>
                      <span>'nı kabul ediyorum</span>
                    </Box>
                  </Typography>
                }
              />
            </Box>

            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.acceptEmails}
                    onChange={(e) => setFormData({...formData, acceptEmails: e.target.checked})}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    E-posta bildirimleri almak istiyorum
                  </Typography>
                }
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                borderRadius: '8px',
                fontWeight: 600
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Hesap Oluştur'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignup}
              sx={{ 
                mb: 2,
                borderRadius: '8px',
                py: 1.5,
                fontWeight: 600
              }}
            >
              Google ile Devam Et
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2">
                Zaten hesabınız var mı?{' '}
                <Link 
                  to="/login" 
                  style={{ 
                    color: 'inherit',
                    textDecoration: 'none',
                    fontWeight: 600,
                    position: 'relative',
                    display: 'inline-block'
                  }}
                  sx={{
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: '0%',
                      height: '2px',
                      bottom: -2,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'primary.main',
                      transition: 'width 0.3s ease-in-out'
                    },
                    '&:hover::after': {
                      width: '100%'
                    }
                  }}
                >
                  Giriş Yap
                </Link>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
    </ThemeProvider>
  );
}
